import moment from 'moment';

export const stringToColor = (str, { dark = false } = {}) => {
    if (!str) {
        return null;
    }
    let hash = 0;
    str.split('').forEach(char => {
        hash = char.charCodeAt(0) + ((hash << 5) - hash);
    })
    let color = '#'
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xff;

        if (dark) {
            value = Math.floor(128 + value * 0.5);
        }

        color += value.toString(16).padStart(2, '0');
    }

    return color + (dark ? 'CC' : '88');
};

export const ensureContrast = (color, backgroundColor = '#1a2332', minContrast = 3) => {
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const getLuminance = (r, g, b) => {
        const [rs, gs, bs] = [r, g, b].map(c => {
            c = c / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };

    const getContrastRatio = (lum1, lum2) => {
        const lighter = Math.max(lum1, lum2);
        const darker = Math.min(lum1, lum2);
        return (lighter + 0.05) / (darker + 0.05);
    };

    const rgbToHsl = (r, g, b) => {
        r /= 255;
        g /= 255;
        b /= 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
                default: break;
            }
        }
        return { h: h * 360, s: s * 100, l: l * 100 };
    };

    const hslToRgb = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;
        let r, g, b;

        if (s === 0) {
            r = g = b = l;
        } else {
            const hue2rgb = (p, q, t) => {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    };

    const rgbToHex = (r, g, b) => {
        return '#' + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('');
    };

    const bgRgb = hexToRgb(backgroundColor);
    const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);

    const colorRgb = hexToRgb(color);
    if (!colorRgb) return color;

    let colorLuminance = getLuminance(colorRgb.r, colorRgb.g, colorRgb.b);
    let contrast = getContrastRatio(bgLuminance, colorLuminance);

    if (contrast >= minContrast) {
        return color;
    }

    const hsl = rgbToHsl(colorRgb.r, colorRgb.g, colorRgb.b);

    let adjustedL = hsl.l;
    let adjustedRgb;
    let attempts = 0;

    while (contrast < minContrast && attempts < 100) {
        adjustedL = Math.min(adjustedL + 2, 95);
        adjustedRgb = hslToRgb(hsl.h, hsl.s, adjustedL);
        colorLuminance = getLuminance(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
        contrast = getContrastRatio(bgLuminance, colorLuminance);
        attempts++;
    }

    return rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
};

export const countWorkingDays = (startDate, endDate) => {
    const day = moment(startDate);
    let workingDays = 0;

    while (day.isSameOrBefore(endDate, 'day')) {
        if (day.day() !== 0 && day.day() !== 6) {
            workingDays++;
        }
        day.add(1, 'd');
    }
    return workingDays;
};